import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Grid, GridItem } from "components/Grid"
import Select from "components/Select/Select"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import TenantEmployee from "models/TenantEmployee"
import { ISelectOption } from "./SelectSearch"
import { Guid, IMultipleEntities } from "@/types"

export interface ISelectEmployeeOptionProps {
    employee?: TenantEmployee
    highlight?: string
}

export const SelectEmployeeOption: FC<React.PropsWithChildren<ISelectEmployeeOptionProps>> = function ({
    employee,
    highlight,
}) {
    if (!employee) {
        return null
    }
    return (
        <Grid
            columns="minmax(0, auto) max-content"
            justifyContent="space-between">
            <FlexibleContent
                text={employee.fullName}
                highlight={highlight} />
            <GridItem className="color-grey-500">
                <FlexibleContent
                    text={employee.number}
                    highlight={highlight} />
            </GridItem>
        </Grid>
    )
}

export interface ISelectEmployeeProps {
    employees?: TenantEmployee[]
    selectedEmployeeId?: Guid
    selectedEmployeeNumber?: string
    isInvalid?: boolean
    isDisabled?: boolean
    onChange(employee?: TenantEmployee): void
}

const SelectEmployee: FC<React.PropsWithChildren<ISelectEmployeeProps>> = ({
    employees,
    selectedEmployeeId,
    selectedEmployeeNumber,
    isInvalid,
    isDisabled,
    onChange,
}) => {
    const { t } = useTranslation()

    const value = useMemo(() => {
        if (!employees) return

        if (selectedEmployeeNumber) {
            return employees?.find(e => e.number === selectedEmployeeNumber)
        }

        if (selectedEmployeeId) {
            return employees?.find(e => e.id === selectedEmployeeId)
        }
    }, [employees, selectedEmployeeId, selectedEmployeeNumber])

    async function getOptions(searchQuery?: string): Promise<IMultipleEntities<TenantEmployee>> {
        const search = searchQuery?.trim().toLowerCase()
        if (!employees || employees.length === 0) return { list: [] }
        if (!search) return { list: employees }
        return {
            list: employees.filter(te => {
                return te.fullName.toLowerCase().includes(search)
                    || te.forename.toLowerCase().includes(search)
                    || te.surname.toLowerCase().includes(search)
                    || te.number.toLowerCase().includes(search)
            })
        }
    }

    function getOptionTemplate(option: ISelectOption<TenantEmployee>, search?: string) {
        return <SelectEmployeeOption employee={option.value} highlight={search} />
    }

    function handleChange(option: ISelectOption<TenantEmployee> | undefined) {
        onChange(option?.value)
    }

    function formatOptionLabel(employee?: TenantEmployee) {
        return employee ? employee.print() : ""
    }

    return (
        <Select
            key={`employee-${selectedEmployeeNumber || selectedEmployeeId || 'none'}-${employees?.length || 0}`}
            defaultValue={value ? { label: formatOptionLabel(value), value } : undefined}
            onChange={handleChange}
            getOptions={getOptions}
            optionTemplate={getOptionTemplate}
            formatOptionLabel={formatOptionLabel}
            isInvalid={isInvalid}
            isClearable
            isDisabled={!employees || isDisabled}
            placeholder={!employees ? t("common.loading") : undefined}
            menuPlacement="bottom"
            testIdPrefix="employee-select"
        />
    )
}

export default SelectEmployee
